/**
 * Maps intake form values to pVerify EligibilitySummary JSON (see pVerify / Postman samples).
 * Dates: HTML `yyyy-mm-dd` → `mm/dd/yyyy` as commonly expected by pVerify examples.
 */

import { providerConfig } from '../data/providerConfig'
import {
  formatUsAddressDisplay,
  parseUsMailingAddress,
} from '../utils/usAddress'

export type IntakeEligibilityForm = {
  patientFirstName: string
  patientLastName: string
  patientDOB: string
  patientGender: string
  patientZip: string
  patientPhone: string
  /** Full mailing address from driving license / intake (street, city, state, zip). */
  patientAddress?: string
  patientCity?: string
  patientState?: string
  patientEmail: string
  subscriberFirstName: string
  subscriberLastName: string
  subscriberDOB: string
  subscriberID: string
  /** Insurance ID of the selected patient (subscriber or dependent). */
  memberID?: string
  relationToSubscriber: string
  subscriberZip: string
  payor: string
  /** pVerify payer code from their payer list (entered in Insurance Details) */
  payerCode: string
  groupNumber: string
  policyNumber: string
  cptHcpcCode: string
  serviceTypeCodes?: string[]
  npi: string
  /** Rendering / NPPES — pVerify `provider` uses these when set. */
  providerFirstName: string
  providerLastName: string
  serviceDateFrom: string
  serviceDateTo: string
  diagnosisCode: string
  placeOfService: string
}

function formatDosForPverify(isoYmd: string): string {
  const raw = isoYmd?.trim()
  if (!raw) return ''
  const parts = raw.split('-')
  if (parts.length === 3) {
    const [y, m, d] = parts
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`
  }
  return raw
}

export type PverifyEligibilityBuildOptions = {
  /** Same as UI “subscriber is the patient” */
  isSubscriberPatient: boolean
}

export function resolveIsSubscriberPatient(
  data: Pick<
    IntakeEligibilityForm,
    | 'patientFirstName'
    | 'patientLastName'
    | 'patientDOB'
    | 'subscriberFirstName'
    | 'subscriberLastName'
    | 'subscriberDOB'
    | 'relationToSubscriber'
  >,
  options?: PverifyEligibilityBuildOptions
): boolean {
  const relation = data.relationToSubscriber.trim().toLowerCase()
  if (relation && relation !== 'self') return false

  const patientName = `${data.patientFirstName} ${data.patientLastName}`.trim().toLowerCase()
  const subscriberName = `${data.subscriberFirstName} ${data.subscriberLastName}`.trim().toLowerCase()
  if (subscriberName && patientName && subscriberName !== patientName) return false

  const patientDob = data.patientDOB.trim()
  const subscriberDob = data.subscriberDOB.trim()
  if (subscriberDob && patientDob && subscriberDob !== patientDob) return false

  return options?.isSubscriberPatient !== false
}

export type ResolvedPatientAddress = {
  street: string
  city: string
  state: string
  zip: string
  display: string
}

export function resolvePatientAddress(data: IntakeEligibilityForm): ResolvedPatientAddress {
  const street = (data.patientAddress ?? '').trim()
  const city = (data.patientCity ?? '').trim()
  const state = (data.patientState ?? '').trim()
  const zip = (data.patientZip ?? '').trim()
  if (city || state || zip) {
    const display =
      formatUsAddressDisplay({ street, city, state, zip }) || street
    return { street, city, state, zip, display }
  }
  if (street) {
    const parsed = parseUsMailingAddress(street)
    return {
      street: parsed.street || street,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip || zip,
      display: parsed.display || street,
    }
  }
  return { street: '', city: '', state: '', zip, display: zip }
}

export function toStediGenderCode(gender: string): 'M' | 'F' | 'U' | undefined {
  const value = gender.trim().toLowerCase()
  if (value === 'male' || value === 'm') return 'M'
  if (value === 'female' || value === 'f') return 'F'
  if (value === 'other' || value === 'u' || value === 'unknown' || value === 'x') return 'U'
  return undefined
}

/** Eligibility 270/271 payloads typically send names in uppercase. */
export function toEligibilityName(value: string): string {
  return value.trim().toUpperCase()
}

/**
 * HIPAA X12 relationship codes used by pVerify `patientRelationship`.
 * 18 Self, 01 Spouse, 19 Child, G8 Other Relationship.
 */
export function toPverifyPatientRelationshipCode(relation: string): string {
  const value = relation.trim().toLowerCase()
  if (value === 'self') return '18'
  if (value === 'spouse') return '01'
  if (value === 'child') return '19'
  return 'G8'
}

export function resolveSubscriberMemberId(data: {
  subscriberID?: string
  policyNumber?: string
}): string {
  return (data.subscriberID || data.policyNumber || '').replace(/\s+/g, '').trim()
}

/** ID of the selected patient. Falls back to the subscriber ID when none is stored. */
export function resolveSelectedMemberId(data: {
  memberID?: string
  subscriberID?: string
  policyNumber?: string
}): string {
  const selected = (data.memberID || '').replace(/\s+/g, '').trim()
  if (selected) return selected
  return resolveSubscriberMemberId(data)
}

export function relationshipToSubscriberLabel(
  data: IntakeEligibilityForm,
  options?: PverifyEligibilityBuildOptions
): 'Self' | 'Dependent' {
  return resolveIsSubscriberPatient(data, options) ? 'Self' : 'Dependent'
}

/** Outer identity fields shared by pVerify / Stedi integration eligibility APIs. */
export function buildEligibilityIntegrationIdentity(
  data: IntakeEligibilityForm,
  options: PverifyEligibilityBuildOptions
): {
  save_patient: true
  memberId: string
  subscriberId: string
  relationshipToSubscriber: 'Self' | 'Dependent'
} {
  return {
    save_patient: true,
    memberId: resolveSelectedMemberId(data),
    subscriberId: resolveSubscriberMemberId(data),
    relationshipToSubscriber: relationshipToSubscriberLabel(data, options),
  }
}

/** Demographics persisted on eligibility-check request_payload (and sent when the vendor supports them). */
export function buildIntakePatientDemographics(
  data: IntakeEligibilityForm
): Record<string, unknown> {
  const address = resolvePatientAddress(data)
  const firstName = data.patientFirstName.trim()
  const lastName = data.patientLastName.trim()
  const dob = data.patientDOB.trim()
  const gender = data.patientGender.trim()
  const phone = data.patientPhone.trim()
  const out: Record<string, unknown> = {}
  if (firstName) out.firstName = firstName
  if (lastName) out.lastName = lastName
  if (dob) out.dateOfBirth = dob
  if (gender) out.gender = gender
  if (phone) out.phone = phone
  if (address.display) out.address = address.display
  if (address.street) out.address1 = address.street
  if (address.city) out.city = address.city
  if (address.state) out.state = address.state
  if (address.zip) out.zip = address.zip
  return out
}

export function buildPverifyEligibilityRequestBody(
  data: IntakeEligibilityForm,
  options: PverifyEligibilityBuildOptions
): Record<string, unknown> {
  const { isSubscriberPatient: isSubscriberPatientOption } = options
  const isSubscriberPatient = resolveIsSubscriberPatient(data, {
    isSubscriberPatient: isSubscriberPatientOption,
  })
  const payerCode = data.payerCode.trim()
  const memberID = resolveSubscriberMemberId(data)
  const serviceCodes = ['30']

  const subscriber = {
    firstName: toEligibilityName(data.subscriberFirstName || data.patientFirstName),
    lastName: toEligibilityName(data.subscriberLastName || data.patientLastName),
    dob: formatDosForPverify(data.subscriberDOB || data.patientDOB),
    memberID,
  }

  const provider = {
    firstName: toEligibilityName(
      data.providerFirstName.trim() || providerConfig.providerFirstName
    ),
    middleName: providerConfig.providerMiddleName,
    lastName: toEligibilityName(
      data.providerLastName.trim() || providerConfig.providerLastName
    ),
    npi: data.npi.trim(),
    pin: providerConfig.providerPin,
  }

  const shared = {
    payerCode,
    payerName: data.payor,
    provider,
    subscriber,
    doS_StartDate: formatDosForPverify(data.serviceDateFrom),
    doS_EndDate: formatDosForPverify(data.serviceDateTo),
    PracticeTypeCode: '3',
    serviceCodes,
  }

  if (isSubscriberPatient) {
    return {
      ...shared,
      dependent: null,
      isSubscriberPatient: 'True',
    }
  }

  const dependentGender = toStediGenderCode(data.patientGender)
  return {
    ...shared,
    dependent: {
      firstName: toEligibilityName(data.patientFirstName),
      lastName: toEligibilityName(data.patientLastName),
      dob: formatDosForPverify(data.patientDOB),
      ...(dependentGender ? { gender: dependentGender } : {}),
      patientRelationship: toPverifyPatientRelationshipCode(data.relationToSubscriber),
    },
    isSubscriberPatient: 'False',
  }
}

export function buildPverifyEligibilityIntegrationBody(
  data: IntakeEligibilityForm,
  options: PverifyEligibilityBuildOptions,
  path: string
): Record<string, unknown> {
  return {
    ...buildEligibilityIntegrationIdentity(data, options),
    payerId: data.payerCode.trim(),
    path,
    payload: buildPverifyEligibilityRequestBody(data, options),
  }
}
